import { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { adminSidebar } from '@/config/sidebarConfig';
import { adminUpdateOrganizationStatusApi, getOrganizationsApi } from '@/lib/api/organizationsApi';
import type { GetOrganizationsParams } from '@/lib/api/organizationsApi';
import { getAdminStatsApi } from '@/lib/api/usersApi';
import { type ExportColumn, type ExportFormat, datedFilename, exportData } from '@/lib/export/export';
import { getPageNumbers } from '@/lib/pagination';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr, getNameInitials } from '@shared';
import { StripeAccountStatus, SubPlan, SubscriptionStatus } from '@shared/enums/domain.enum';
import type { Organization } from '@shared/types/entities';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
  Briefcase,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Download,
  ExternalLink,
  Eye,
  MapPin,
  Power,
  Search,
  ShieldCheck,
  Zap,
} from 'lucide-react';

import OrganizerDetailDrawer from '../../components/admin/OrganizerDetailDrawer';
import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';
import StatCard from '../../components/ui/StatCard';

// ─── Helpers ────────────────────────────────────────────────────────────────

const AVATAR_GRADIENTS = [
  'from-amber-500 to-orange-600',
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-purple-500 to-indigo-600',
  'from-rose-500 to-red-600',
  'from-cyan-500 to-blue-600',
  'from-violet-500 to-purple-600',
  'from-lime-500 to-green-600',
];

function orgGradient(id: number): string {
  return AVATAR_GRADIENTS[id % AVATAR_GRADIENTS.length] ?? AVATAR_GRADIENTS[0];
}

interface BadgeStyle {
  label: string;
  cls: string;
}

function planBadge(plan: SubPlan): BadgeStyle {
  switch (plan) {
    case SubPlan.FORFAIT:
      return { label: 'Forfait', cls: 'bg-amber-100 text-amber-700' };
    case SubPlan.COMMISSION:
    default:
      return { label: 'Commission', cls: 'bg-slate-100 text-slate-600' };
  }
}

function subStatusBadge(status: SubscriptionStatus): BadgeStyle {
  switch (status) {
    case SubscriptionStatus.ACTIVE:
      return { label: 'Actif', cls: 'bg-emerald-100 text-emerald-700' };
    case SubscriptionStatus.PAST_DUE:
      return { label: 'Retard CB', cls: 'bg-red-100 text-red-700' };
    case SubscriptionStatus.UNPAID:
      return { label: 'Impayé', cls: 'bg-red-100 text-red-700' };
    case SubscriptionStatus.CANCELED:
    default:
      return { label: 'Résilié', cls: 'bg-slate-100 text-slate-500' };
  }
}

function connectBadge(org: Organization): BadgeStyle | null {
  if (!org.stripeConnectAccountId) return null;
  switch (org.stripeAccountStatus) {
    case StripeAccountStatus.ACTIVE:
      return { label: 'Connect actif', cls: 'bg-emerald-100 text-emerald-700' };
    case StripeAccountStatus.ONBOARDING:
      return { label: 'Onboarding', cls: 'bg-blue-100 text-blue-700' };
    case StripeAccountStatus.RESTRICTED:
      return { label: 'Restreint', cls: 'bg-amber-100 text-amber-700' };
    case StripeAccountStatus.DISABLED:
      return { label: 'Désactivé', cls: 'bg-red-100 text-red-700' };
    default:
      return { label: 'Connect', cls: 'bg-slate-100 text-slate-500' };
  }
}

type TabKey = 'all' | 'active' | 'forfait' | 'inactive';
type SortKey = 'date_desc' | 'name_asc' | 'name_desc';

const VALID_TABS = new Set<string>(['all', 'active', 'forfait', 'inactive']);
const VALID_SORTS = new Set<string>(['date_desc', 'name_asc', 'name_desc']);

const ITEMS_PER_PAGE = 25;

// ─── Tab → API params mapping ─────────────────────────────────────────────

function tabToApiParams(tab: TabKey): Pick<GetOrganizationsParams, 'subscriptionStatus' | 'plan'> {
  switch (tab) {
    case 'active':
      return { subscriptionStatus: SubscriptionStatus.ACTIVE };
    case 'forfait':
      return { plan: SubPlan.FORFAIT };
    case 'inactive':
      return {
        subscriptionStatus: `${SubscriptionStatus.PAST_DUE},${SubscriptionStatus.UNPAID},${SubscriptionStatus.CANCELED}`,
      };
    case 'all':
    default:
      return {};
  }
}

function sortKeyToApiParams(sortKey: SortKey): Pick<GetOrganizationsParams, 'sortBy' | 'sortOrder'> {
  switch (sortKey) {
    case 'name_asc':
      return { sortBy: 'name', sortOrder: 'asc' };
    case 'name_desc':
      return { sortBy: 'name', sortOrder: 'desc' };
    case 'date_desc':
    default:
      return { sortBy: 'createdAt', sortOrder: 'desc' };
  }
}

// ─── CSV Export (on-demand, all pages) ───────────────────────────────────────

async function fetchAllForExport(params: GetOrganizationsParams): Promise<Organization[]> {
  const limit = 100;
  let p = 1;
  const all: Organization[] = [];
  while (true) {
    const res = await getOrganizationsApi({ ...params, limit, page: p });
    all.push(...res.data);
    if (p >= res.meta.totalPages) break;
    p++;
  }
  return all;
}

const ORG_EXPORT_COLUMNS: ExportColumn<Organization>[] = [
  { header: 'ID', value: 'id' },
  { header: 'Nom', value: 'name' },
  { header: 'Alias', value: 'alias' },
  { header: 'Ville', value: o => o.city ?? '' },
  { header: 'Département', value: o => o.department ?? '' },
  { header: 'Plan', value: 'plan' },
  { header: 'Active', value: o => (o.isActive ? 'oui' : 'non') },
  { header: 'Abonnement', value: 'subscriptionStatus' },
  { header: 'Connect', value: 'stripeAccountStatus' },
  { header: 'Crédit commission (€)', value: o => (o.commissionCreditRemainingCents / 100).toFixed(2) },
  { header: 'Inscrit le', value: o => o.createdAt.slice(0, 10) },
];

// ─── URL param helpers ────────────────────────────────────────────────────────

function applyParamUpdates(prev: URLSearchParams, updates: Record<string, string | undefined>): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined || value === '') next.delete(key);
    else next.set(key, value);
  }
  return next;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function AdminOrganizers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  // ─── Read filters from URL ───
  const urlTab = searchParams.get('tab') ?? 'all';
  const urlSort = searchParams.get('sort') ?? 'date_desc';
  const urlPage = Math.max(1, Number(searchParams.get('page') ?? '1'));

  const tab = (VALID_TABS.has(urlTab) ? urlTab : 'all') as TabKey;
  const sortKey = (VALID_SORTS.has(urlSort) ? urlSort : 'date_desc') as SortKey;
  const page = urlPage;

  // ─── searchInput is local (debounce buffer); search is URL-persisted ───
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const search = searchParams.get('search') ?? '';

  const [isExporting, setIsExporting] = useState(false);
  const [detailOrgId, setDetailOrgId] = useState<number | null>(null);

  function updateParams(updates: Record<string, string | undefined>) {
    setSearchParams(prev => applyParamUpdates(prev, updates), { replace: true });
  }

  const queryParams: GetOrganizationsParams = {
    page,
    limit: ITEMS_PER_PAGE,
    ...(search ? { search } : {}),
    ...sortKeyToApiParams(sortKey),
    ...tabToApiParams(tab),
  };

  const {
    data: orgsResponse,
    isLoading: orgsLoading,
    isPlaceholderData,
    isError: orgsError,
    refetch: refetchOrgs,
  } = useQuery({
    queryKey: queryKeys.organizations.list(queryParams as Record<string, unknown>),
    queryFn: () => getOrganizationsApi(queryParams),
    enabled: isAuthenticated,
    placeholderData: keepPreviousData,
    staleTime: 2 * 60 * 1000,
  });

  const { data: platformStats } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: getAdminStatsApi,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = orgsLoading || !isAuthenticated;

  const orgs = useMemo<Organization[]>(() => orgsResponse?.data ?? [], [orgsResponse]);
  const meta = orgsResponse?.meta;

  const totalPages = meta?.totalPages ?? 1;
  const totalCount = meta?.total ?? 0;
  const pageNumbers = getPageNumbers(page, totalPages);
  const hasFilters = search !== '' || tab !== 'all' || sortKey !== 'date_desc';

  function handleSearch(value: string) {
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateParams({ search: value.trim() || undefined, page: undefined });
    }, 350);
  }

  function handleTab(t: TabKey) {
    updateParams({ tab: t !== 'all' ? t : undefined, page: undefined });
  }

  function handleSort(s: SortKey) {
    updateParams({ sort: s !== 'date_desc' ? s : undefined, page: undefined });
  }

  function handleClearFilters() {
    setSearchInput('');
    updateParams({ search: undefined, tab: undefined, sort: undefined, page: undefined });
  }

  // Activation / desactivation (SUPER_ADMIN). Volontairement distinct de
  // l'onglet « Inactifs », qui porte sur le statut d'ABONNEMENT et non sur
  // isActive : une organisation à jour de paiement peut être désactivée, et
  // inversement. On rafraîchit via refetchOrgs plutôt que d'invalider une clé
  // de cache, la requête courante dépendant des filtres d'URL.
  const statusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) => adminUpdateOrganizationStatusApi(id, isActive),
    onSuccess: () => void refetchOrgs(),
  });

  function handleToggleActive(org: Organization) {
    const next = !org.isActive;
    const message = next
      ? `Réactiver « ${org.name} » ? Sa fiche publique et son blog redeviendront visibles, et ses administrateurs pourront se reconnecter.`
      : `Désactiver « ${org.name} » ? Sa fiche publique, son blog et les couvertures de ses articles seront masqués, et ses administrateurs ne pourront plus se connecter.`;
    if (!window.confirm(message)) return;
    statusMutation.mutate({ id: org.id, isActive: next });
  }

  async function handleExport(format: ExportFormat = 'csv') {
    setIsExporting(true);
    try {
      const exportParams: GetOrganizationsParams = {
        ...(search ? { search } : {}),
        ...sortKeyToApiParams(sortKey),
        ...tabToApiParams(tab),
      };
      const allOrgs = await fetchAllForExport(exportParams);
      await exportData(allOrgs, ORG_EXPORT_COLUMNS, datedFilename('organisateurs', format), format, {
        sheetName: 'Organisateurs',
      });
    } finally {
      setIsExporting(false);
    }
  }

  const orgStats = platformStats?.organizations;
  const frontUrl =
    (import.meta.env.VITE_URL_FRONT as string | undefined)?.replace(/\/$/, '') ?? 'http://localhost:5232';

  // Tab counts derived from platformStats
  const counts = useMemo(
    () => ({
      all: orgStats?.total ?? 0,
      active: orgStats?.bySubscriptionStatus.ACTIVE ?? 0,
      forfait: orgStats?.byPlan.FORFAIT ?? 0,
      inactive:
        (orgStats?.bySubscriptionStatus.PAST_DUE ?? 0) +
        (orgStats?.bySubscriptionStatus.UNPAID ?? 0) +
        (orgStats?.bySubscriptionStatus.CANCELED ?? 0),
    }),
    [orgStats],
  );

  // KPI stats
  const statCards = [
    {
      icon: Briefcase,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      label: 'Organisateurs',
      value: String(orgStats?.total ?? 0),
      trend: orgStats?.newThisMonth ? `+${orgStats.newThisMonth} ce mois` : undefined,
    },
    {
      icon: CreditCard,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      label: 'Abonnés actifs',
      value: String(orgStats?.bySubscriptionStatus.ACTIVE ?? 0),
    },
    {
      icon: Zap,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-500',
      label: 'Plan Forfait',
      value: String(orgStats?.byPlan.FORFAIT ?? 0),
      subtitle: 'Commission : ' + String(orgStats?.byPlan.COMMISSION ?? 0),
    },
    {
      icon: ShieldCheck,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      label: 'Connect Stripe actif',
      value: String(orgStats?.connectActive ?? 0),
      subtitle: `${orgStats?.withConnect ?? 0} comptes configurés`,
    },
  ];

  const TABS: { key: TabKey; label: string }[] = [
    { key: 'all', label: 'Tous' },
    { key: 'active', label: 'Actifs' },
    { key: 'forfait', label: 'Forfait' },
    { key: 'inactive', label: 'Inactifs' },
  ];

  return (
    <DashboardLayout sidebarConfig={adminSidebar}>
      <div className='p-8'>
        <PageHeader
          title='Gestion des Organisateurs'
          subtitle='Suivi des comptes organisateurs et abonnements'
          actions={
            <div className='flex items-center gap-2'>
              <button
                onClick={() => void handleExport('csv')}
                disabled={isExporting}
                className='flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition text-sm font-medium text-slate-600 disabled:opacity-50'
                title='Exporter CSV'
              >
                <Download className='w-4 h-4' />
                {isExporting ? 'Export…' : 'CSV'}
              </button>
              <button
                onClick={() => void handleExport('xlsx')}
                disabled={isExporting}
                className='flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition text-sm font-medium text-slate-600 disabled:opacity-50'
                title='Exporter Excel'
              >
                <Download className='w-4 h-4' />
                {isExporting ? 'Export…' : 'Excel'}
              </button>
            </div>
          }
        />

        {/* KPI Cards */}
        <div className='grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
          {statCards.map(s => (
            <StatCard
              key={s.label}
              icon={s.icon}
              iconBg={s.iconBg}
              iconColor={s.iconColor}
              label={s.label}
              value={s.value}
              trend={s.trend}
              subtitle={s.subtitle}
            />
          ))}
        </div>

        {/* Filters */}
        <div className='bg-white rounded-xl border border-slate-200 p-4 mb-4'>
          <div className='flex flex-wrap items-center gap-3'>
            <div className='relative flex-1 min-w-[200px] max-w-sm'>
              <Search className='w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2' />
              <input
                type='text'
                value={searchInput}
                onChange={e => handleSearch(e.target.value)}
                placeholder='Rechercher par nom, alias, ville…'
                className='w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none'
              />
            </div>

            <select
              value={sortKey}
              onChange={e => handleSort(e.target.value as SortKey)}
              className='px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none'
            >
              <option value='date_desc'>Plus récents</option>
              <option value='name_asc'>Nom A → Z</option>
              <option value='name_desc'>Nom Z → A</option>
            </select>

            {hasFilters && (
              <button
                onClick={handleClearFilters}
                className='px-3 py-2 text-sm text-slate-500 hover:text-slate-700 border border-dashed border-slate-300 rounded-lg transition'
              >
                Effacer les filtres
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className='flex gap-1 mb-4 border-b border-slate-200'>
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => handleTab(t.key)}
              className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg transition flex items-center gap-2 ${
                tab === t.key
                  ? 'bg-white border border-b-white border-slate-200 text-blue-600 -mb-px'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {t.label}
              <span
                className={`text-xs rounded-full px-2 py-0.5 ${
                  tab === t.key ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                }`}
              >
                {counts[t.key]}
              </span>
            </button>
          ))}
        </div>

        {orgsError && <QueryErrorState onRetry={() => void refetchOrgs()} />}

        {/* Table */}
        <div className={`bg-white rounded-xl border border-slate-200 ${isPlaceholderData ? 'opacity-70' : ''}`}>
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead className='bg-slate-50 border-b border-slate-200'>
                <tr>
                  <th className='px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase'>Organisation</th>
                  <th className='px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase'>Plan</th>
                  <th className='px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase'>Abonnement</th>
                  <th className='px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase'>Connect Stripe</th>
                  <th className='px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase'>Inscrit le</th>
                  <th className='px-5 py-3 text-right text-xs font-semibold text-slate-500 uppercase'>Actions</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-slate-100'>
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className='animate-pulse'>
                      <td className='px-5 py-4'>
                        <div className='flex items-center gap-3'>
                          <div className='w-9 h-9 rounded-full bg-slate-100' />
                          <div className='space-y-1'>
                            <div className='h-3.5 w-32 bg-slate-100 rounded-full' />
                            <div className='h-3 w-20 bg-slate-100 rounded-full' />
                          </div>
                        </div>
                      </td>
                      {Array.from({ length: 4 }).map((__, j) => (
                        <td key={j} className='px-5 py-4'>
                          <div className='h-5 w-20 bg-slate-100 rounded-full' />
                        </td>
                      ))}
                      <td className='px-5 py-4' />
                    </tr>
                  ))
                ) : orgs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className='px-5 py-16 text-center text-slate-400'>
                      Aucun organisateur trouvé.
                    </td>
                  </tr>
                ) : (
                  orgs.map(org => {
                    const plan = planBadge(org.plan);
                    const sub = subStatusBadge(org.subscriptionStatus);
                    const connect = connectBadge(org);
                    // Narrowing direct (vs `.includes()`) : depuis que
                    // `SubscriptionStatus` est une union de literals (cf.
                    // `domain.enum.ts`), TS infère `[PAST_DUE, UNPAID]`
                    // comme `("PAST_DUE" | "UNPAID")[]` et refuse un argument
                    // de l'union plus large. Comparaisons explicites = zéro
                    // cast + meilleur narrowing.
                    const isProblem =
                      org.subscriptionStatus === SubscriptionStatus.PAST_DUE ||
                      org.subscriptionStatus === SubscriptionStatus.UNPAID;

                    return (
                      <tr
                        key={org.id}
                        className={`hover:bg-slate-50 transition-colors ${isProblem ? 'bg-red-50/40' : ''}`}
                      >
                        {/* Organisation */}
                        <td className='px-5 py-3.5'>
                          <div className='flex items-center gap-3'>
                            <div
                              className={`w-9 h-9 flex-shrink-0 rounded-full bg-gradient-to-br ${orgGradient(org.id)} flex items-center justify-center text-white text-xs font-bold`}
                            >
                              {getNameInitials(org.name)}
                            </div>
                            <div className='min-w-0'>
                              <div className='flex items-center gap-2'>
                                <p className='font-semibold text-slate-900 truncate max-w-[180px]'>{org.name}</p>
                                {!org.isActive && (
                                  <span className='inline-flex flex-shrink-0 items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700'>
                                    Désactivée
                                  </span>
                                )}
                              </div>
                              <p className='text-xs text-slate-400 flex items-center gap-1 truncate'>
                                {org.city && (
                                  <>
                                    <MapPin className='w-3 h-3 flex-shrink-0' />
                                    {org.city}
                                    {org.department && ` (${org.department})`}
                                  </>
                                )}
                                {!org.city && <span className='font-mono text-slate-300'>#{org.alias}</span>}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Plan */}
                        <td className='px-5 py-3.5'>
                          <div className='flex flex-col gap-1'>
                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${plan.cls}`}
                            >
                              {org.plan === SubPlan.FORFAIT && <Zap className='w-3 h-3' />}
                              {plan.label}
                            </span>
                            {org.commissionCreditRemainingCents > 0 && (
                              <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700'>
                                Crédit : {(org.commissionCreditRemainingCents / 100).toFixed(2).replace('.', ',')} €
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Abonnement */}
                        <td className='px-5 py-3.5'>
                          <span
                            className={`inline-flex w-fit items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${sub.cls}`}
                          >
                            {sub.label}
                          </span>
                        </td>

                        {/* Connect Stripe */}
                        <td className='px-5 py-3.5'>
                          {connect ? (
                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${connect.cls}`}
                            >
                              <ShieldCheck className='w-3 h-3' />
                              {connect.label}
                            </span>
                          ) : (
                            <span className='text-slate-300 text-xs'>—</span>
                          )}
                        </td>

                        {/* Inscrit le */}
                        <td className='px-5 py-3.5 text-slate-500 text-xs whitespace-nowrap'>
                          {formatDateAbbreviatedFr(org.createdAt)}
                        </td>

                        {/* Actions */}
                        <td className='px-5 py-3.5'>
                          <div className='flex items-center justify-end gap-1'>
                            <button
                              onClick={() => handleToggleActive(org)}
                              disabled={statusMutation.isPending}
                              className={`p-2 hover:bg-slate-100 rounded-lg transition disabled:opacity-40 ${org.isActive ? 'text-slate-400 hover:text-red-600' : 'text-red-500 hover:text-emerald-600'}`}
                              title={org.isActive ? 'Désactiver cette organisation' : 'Réactiver cette organisation'}
                            >
                              <Power className='w-4 h-4' />
                            </button>
                            <button
                              onClick={() => setDetailOrgId(org.id)}
                              className='p-2 hover:bg-slate-100 rounded-lg transition text-slate-400 hover:text-amber-600'
                              title='Gérer / détail'
                            >
                              <Eye className='w-4 h-4' />
                            </button>
                            <a
                              href={`${frontUrl}/organisateurs/${org.id}`}
                              target='_blank'
                              rel='noreferrer'
                              className='p-2 hover:bg-slate-100 rounded-lg transition text-slate-400 hover:text-blue-600'
                              title='Voir le profil public'
                            >
                              <ExternalLink className='w-4 h-4' />
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!isLoading && totalCount > 0 && (
            <div className='px-5 py-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3'>
              <p className='text-sm text-slate-500'>
                {totalCount} organisateur{totalCount > 1 ? 's' : ''}
              </p>
              {totalPages > 1 && (
                <div className='flex items-center gap-1'>
                  <button
                    onClick={() => updateParams({ page: String(Math.max(1, page - 1)) })}
                    disabled={page === 1 || isPlaceholderData}
                    className='px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition disabled:opacity-40 flex items-center gap-1 text-sm'
                  >
                    <ChevronLeft className='w-4 h-4' />
                  </button>
                  {pageNumbers.map((n, i) =>
                    n === '...' ? (
                      <span key={`ellipsis-${i}`} className='px-3 py-2 text-slate-400'>
                        …
                      </span>
                    ) : (
                      <button
                        key={n}
                        onClick={() => updateParams({ page: String(n) })}
                        disabled={isPlaceholderData}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                          page === n ? 'bg-blue-600 text-white' : 'border border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        {n}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() => updateParams({ page: String(Math.min(totalPages, page + 1)) })}
                    disabled={page === totalPages || isPlaceholderData}
                    className='px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition disabled:opacity-40 flex items-center gap-1 text-sm'
                  >
                    <ChevronRight className='w-4 h-4' />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <OrganizerDetailDrawer orgId={detailOrgId} onClose={() => setDetailOrgId(null)} frontUrl={frontUrl} />
    </DashboardLayout>
  );
}
